import { useEffect, useId, useRef, useState } from "react";

import { aiExamples } from "./aiExampleTemplates";

function PromptCard({ text }: { text: string }) {
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle");
  useEffect(() => {
    if (status !== "copied") return;
    const timer = window.setTimeout(() => setStatus("idle"), 2000);
    return () => window.clearTimeout(timer);
  }, [status]);
  async function copyPrompt() {
    try {
      await navigator.clipboard.writeText(text);
      setStatus("copied");
    } catch {
      setStatus("failed");
    }
  }
  return (
    <div className="ai-prompt-card">
      <div className="ai-prompt ai-copy-box">
        <div className="ai-copy-content">{text}</div>
        <button
          type="button"
          className="ai-text-link"
          onClick={() => void copyPrompt()}
          aria-live="polite"
        >
          {status === "copied" ? "Copied!" : "Copy prompt"}
        </button>
      </div>
      {status === "failed" && <p role="alert">Copy failed. Select and copy the prompt.</p>}
    </div>
  );
}

export function AiExamples({ agency = false }: { agency?: boolean }) {
  const [selected, setSelected] = useState(0);
  const id = useId();
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);
  return (
    <div className="ai-scenarios">
      <section aria-labelledby={`${id}-create`}>
        <h3 id={`${id}-create`}>Ask Copilot to create a new diagram.</h3>
        <p className="ai-example-instruction">
          {agency
            ? "Paste a prompt into your Agency-launched Copilot session, or write your own."
            : "Try these prompt templates in GitHub Copilot, or write your own."}
        </p>
        <div className="ai-example-panel">
          <div className="ai-example-tabs" role="tablist" aria-label="New drawing examples">
            {aiExamples.map((example, index) => (
              <button
                key={example.label}
                ref={(element) => {
                  tabs.current[index] = element;
                }}
                type="button"
                role="tab"
                id={`${id}-tab-${index}`}
                aria-controls={`${id}-panel-${index}`}
                aria-selected={selected === index}
                tabIndex={selected === index ? 0 : -1}
                onClick={() => setSelected(index)}
                onKeyDown={(event) => {
                  let next: number;
                  if (event.key === "ArrowRight") next = (index + 1) % aiExamples.length;
                  else if (event.key === "ArrowLeft")
                    next = (index + aiExamples.length - 1) % aiExamples.length;
                  else if (event.key === "Home") next = 0;
                  else if (event.key === "End") next = aiExamples.length - 1;
                  else return;
                  event.preventDefault();
                  setSelected(next);
                  tabs.current[next]?.focus();
                }}
              >
                {example.label}
              </button>
            ))}
          </div>
          {aiExamples.map((example, index) => (
            <div
              key={example.label}
              id={`${id}-panel-${index}`}
              role="tabpanel"
              aria-labelledby={`${id}-tab-${index}`}
              hidden={selected !== index}
              tabIndex={0}
            >
              <PromptCard text={example.prompt} />
            </div>
          ))}
        </div>
      </section>
      <section className="ai-edit-scenario" aria-labelledby={`${id}-edit`}>
        <h3 id={`${id}-edit`}>Ask Copilot to edit an existing drawing.</h3>
        <p className="ai-example-instruction">
          Open a cloud drawing, choose <strong>Share → Share with AI</strong>, and paste the
          prepared prompt into Copilot with your edit request.
        </p>
        <div className="ai-edit-example">
          <p className="ai-example-label">Prompt prepared by Share with AI</p>
          <div className="ai-prompt">
            {
              'Use the Excalidraw-MS tools to open my drawing "<drawing name>" with code <AI access code>, then '
            }
          </div>
        </div>
      </section>
    </div>
  );
}
